import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Zap, Cpu, Globe, Database, Smartphone, Layers, CheckCircle2, ArrowRight } from 'lucide-react';
import { PageWrapper, SectionWrapper } from '../components/MotionWrappers';
export function ComplyPlus() {
  const architectures = [{
    title: 'Native Website Modules',
    icon: Globe,
    desc: 'Directly plugs into Drupal, WordPress, and major CMS architectures to act as an automated gatekeeper.',
    features: ['Decoupled & Native Integration', 'Inline Interception', 'Automated Gatekeeping']
  }, {
    title: 'Enterprise API Middleware',
    icon: Cpu,
    desc: 'Platform-agnostic engine designed for custom web applications and centralized infrastructure.',
    features: ['Secure REST Endpoints', 'High-Volume Processing', 'Programmatic Submission']
  }, {
    title: 'Standalone Applications',
    icon: Smartphone,
    desc: 'Dedicated desktop and mobile interfaces for manual batch-processing and field remediation.',
    features: ['On-Demand Local Uploads', 'Field Remediation', 'Cloud-Powered Formatting']
  }];
  const capabilities = [{
    title: 'Autonomous Legacy Scanning',
    subtitle: 'Backlog Remediation',
    icon: Database,
    items: ['Deep-Directory Crawling: Automated background workers audit existing directories.', 'Unremediated Asset Identification: Flags untagged PDFs and missing metadata.', 'Autonomous Correction Engine: Programmatically injects structural tags and semantic data.']
  }, {
    title: 'Real-Time Active Guard',
    subtitle: 'New Media Remediation',
    icon: Zap,
    items: ['Zero-Day Compliance: Instantly processes content at the point of ingestion.', 'Pre-Publish Remediation: Ensures no non-compliant media reaches publication.', 'Unbroken Compliance: Maintains a permanent state of accessibility.']
  }];
  return <PageWrapper data-id="element-406">
      <div className="bg-white" data-id="element-407">
        {/* Hero Section */}
        <section className="bg-federal-blue py-24 text-white relative overflow-hidden" data-id="element-408">
          <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-409">
            <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} data-id="element-410" />
          </div>
          <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10" data-id="element-411">
            <div className="max-w-3xl" data-id="element-412">
              <motion.span initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="inline-block px-4 py-1.5 bg-muted-gold/20 text-muted-gold text-xs font-black tracking-widest uppercase rounded-full mb-6 border border-muted-gold/30" data-id="element-413">
                Autonomous Accessibility Platform
              </motion.span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="text-5xl md:text-7xl font-black mb-8 leading-tight" data-id="element-414">
                Comply<span className="text-muted-gold" data-id="element-415">+</span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-xl text-white/80 leading-relaxed mb-10" data-id="element-416">
                An enterprise-grade, autonomous digital accessibility and media
                remediation SaaS platform. Engineered to solve federal DOJ Title
                II mandates and WCAG 2.1 Level AA standards.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="flex flex-wrap gap-4" data-id="element-417">
                <motion.button whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.97
              }} className="bg-muted-gold hover:bg-gold-hover text-white px-8 py-4 font-bold rounded-sm transition-colors flex items-center gap-2" data-id="element-418">
                  Request Technical Specs <ArrowRight className="w-4 h-4" data-id="element-419" />
                </motion.button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Product Overview */}
        <SectionWrapper data-id="element-420">
          <section className="py-20 bg-slate-50" data-id="element-421">
            <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-422">
              <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-423">
                <div data-id="element-424">
                  <h2 className="text-federal-blue text-sm font-black tracking-[0.2em] uppercase mb-6" data-id="element-425">
                    01. Product Overview
                  </h2>
                  <h3 className="text-4xl font-bold text-tech-slate mb-8 leading-tight" data-id="element-426">
                    Eliminating the high cost of <br data-id="element-427" />
                    <span className="text-federal-blue" data-id="element-428">
                      manual remediation.
                    </span>
                  </h3>
                  <p className="text-lg text-tech-slate/80 leading-relaxed mb-6" data-id="element-429">
                    Comply+ operates via a multi-channel architecture—deployable
                    as a native website plugin, an integrated API middleware, or
                    a standalone application.
                  </p>
                  <div className="space-y-4" data-id="element-430">
                    <div className="flex items-start gap-3" data-id="element-431">
                      <CheckCircle2 className="w-5 h-5 text-muted-gold mt-1" data-id="element-432" />
                      <p className="font-medium text-tech-slate" data-id="element-433">
                        Continuous Real-Time Interception
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-434">
                      <CheckCircle2 className="w-5 h-5 text-muted-gold mt-1" data-id="element-435" />
                      <p className="font-medium text-tech-slate" data-id="element-436">
                        Autonomous Legacy Backlog Remediation
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-437">
                      <CheckCircle2 className="w-5 h-5 text-muted-gold mt-1" data-id="element-438" />
                      <p className="font-medium text-tech-slate" data-id="element-439">
                        DOJ Title II & WCAG 2.1 AA Compliant
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-federal-blue p-12 rounded-sm shadow-2xl relative transition-transform duration-500 hover:-translate-y-1" data-id="element-440">
                  <div className="absolute top-0 right-0 p-8 opacity-10" data-id="element-441">
                    <Layers className="w-32 h-32 text-white" data-id="element-442" />
                  </div>
                  <div className="relative z-10" data-id="element-443">
                    <h4 className="text-muted-gold font-black text-xs uppercase tracking-widest mb-4" data-id="element-444">
                      Compliance Coverage
                    </h4>
                    <div className="space-y-8" data-id="element-445">
                      <div className="border-l-2 border-muted-gold/30 pl-6" data-id="element-446">
                        <p className="text-white font-bold text-lg mb-2" data-id="element-447">
                          Real-Time Remediation
                        </p>
                        <p className="text-white/60 text-sm" data-id="element-448">
                          Intercepts and fixes newly uploaded assets instantly.
                        </p>
                      </div>
                      <div className="border-l-2 border-muted-gold/30 pl-6" data-id="element-449">
                        <p className="text-white font-bold text-lg mb-2" data-id="element-450">
                          Legacy Backlog
                        </p>
                        <p className="text-white/60 text-sm" data-id="element-451">
                          Background scans autonomously identify and fix
                          historical files.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </SectionWrapper>

        {/* Deployment Architectures */}
        <section className="py-20 bg-white" data-id="element-452">
          <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-453">
            <SectionWrapper data-id="element-454">
              <div className="text-center mb-16" data-id="element-455">
                <h2 className="text-federal-blue text-sm font-black tracking-[0.2em] uppercase mb-4" data-id="element-456">
                  02. Core Deployment
                </h2>
                <h3 className="text-4xl font-bold text-tech-slate" data-id="element-457">
                  Architectural Flexibility
                </h3>
              </div>
            </SectionWrapper>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-458">
              {architectures.map((arch, i) => <SectionWrapper key={i} delay={i * 0.1} data-id="element-459">
                  <div className="p-8 border border-slate-200 rounded-sm hover:border-federal-blue hover:shadow-lg transition-all duration-300 group h-full" data-id="element-460">
                    <div className="w-14 h-14 bg-slate-50 flex items-center justify-center mb-8 group-hover:bg-federal-blue transition-colors" data-id="element-461">
                      <arch.icon className="w-7 h-7 text-federal-blue group-hover:text-white transition-colors" data-id="element-462" />
                    </div>
                    <h4 className="text-xl font-bold text-federal-blue mb-4" data-id="element-463">
                      {arch.title}
                    </h4>
                    <p className="text-sm text-tech-slate/70 mb-8 leading-relaxed" data-id="element-464">
                      {arch.desc}
                    </p>
                    <ul className="space-y-3" data-id="element-465">
                      {arch.features.map((feat, j) => <li key={j} className="text-xs font-bold text-tech-slate flex items-center gap-2" data-id="element-466">
                          <div className="w-1.5 h-1.5 bg-muted-gold rounded-full" data-id="element-467" />
                          {feat}
                        </li>)}
                    </ul>
                  </div>
                </SectionWrapper>)}
            </div>
          </div>
        </section>

        {/* Functional Capabilities */}
        <section className="py-20 bg-federal-blue text-white" data-id="element-468">
          <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-469">
            <SectionWrapper data-id="element-470">
              <div className="text-center mb-16" data-id="element-471">
                <h2 className="text-muted-gold text-sm font-black tracking-[0.2em] uppercase mb-4" data-id="element-472">
                  03. Key Capabilities
                </h2>
                <h3 className="text-4xl font-bold" data-id="element-473">Autonomous Intelligence</h3>
              </div>
            </SectionWrapper>
            <div className="grid lg:grid-cols-2 gap-12" data-id="element-474">
              {capabilities.map((cap, i) => <SectionWrapper key={i} delay={i * 0.1} data-id="element-475">
                  <div className="bg-white/5 border border-white/10 p-10 rounded-sm h-full transition-colors hover:bg-white/10" data-id="element-476">
                    <div className="flex items-center gap-4 mb-8" data-id="element-477">
                      <div className="w-12 h-12 bg-muted-gold/20 flex items-center justify-center rounded-sm" data-id="element-478">
                        <cap.icon className="w-6 h-6 text-muted-gold" data-id="element-479" />
                      </div>
                      <div data-id="element-480">
                        <h4 className="text-xl font-bold" data-id="element-481">{cap.title}</h4>
                        <p className="text-muted-gold text-xs font-black uppercase tracking-widest" data-id="element-482">
                          {cap.subtitle}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-6" data-id="element-483">
                      {cap.items.map((item, j) => {
                    const [title, desc] = item.split(': ');
                    return <div key={j} data-id="element-484">
                            <p className="font-bold text-white mb-1" data-id="element-485">{title}</p>
                            <p className="text-sm text-white/60 leading-relaxed" data-id="element-486">
                              {desc}
                            </p>
                          </div>;
                  })}
                    </div>
                  </div>
                </SectionWrapper>)}
            </div>
          </div>
        </section>

        {/* Technical Specifications */}
        <SectionWrapper data-id="element-487">
          <section className="py-20 bg-slate-50" data-id="element-488">
            <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-489">
              <div className="bg-white border border-slate-200 p-12 md:p-20 rounded-sm shadow-sm" data-id="element-490">
                <div className="max-w-3xl" data-id="element-491">
                  <h2 className="text-federal-blue text-sm font-black tracking-[0.2em] uppercase mb-6" data-id="element-492">
                    04. Technical Specifications
                  </h2>
                  <h3 className="text-4xl font-bold text-tech-slate mb-12" data-id="element-493">
                    Compliance & Standards
                  </h3>

                  <div className="grid md:grid-cols-2 gap-12" data-id="element-494">
                    <div className="space-y-8" data-id="element-495">
                      <div data-id="element-496">
                        <h5 className="text-federal-blue font-black text-xs uppercase tracking-widest mb-3" data-id="element-497">
                          Target Standards
                        </h5>
                        <p className="text-tech-slate font-medium" data-id="element-498">
                          Full automated mapping and remediation to meet WCAG
                          2.1 Level AA and Section 508 compliance parameters.
                        </p>
                      </div>
                      <div data-id="element-499">
                        <h5 className="text-federal-blue font-black text-xs uppercase tracking-widest mb-3" data-id="element-500">
                          Supported File Types
                        </h5>
                        <div className="flex flex-wrap gap-2" data-id="element-501">
                          {['PDF', 'Word', 'PowerPoint', 'Excel', 'Web Images'].map(type => <span key={type} className="px-3 py-1 bg-slate-100 text-tech-slate text-[10px] font-black uppercase rounded-full" data-id="element-502">
                              {type}
                            </span>)}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-8" data-id="element-503">
                      <div data-id="element-504">
                        <h5 className="text-federal-blue font-black text-xs uppercase tracking-widest mb-3" data-id="element-505">
                          Security Architecture
                        </h5>
                        <p className="text-tech-slate font-medium" data-id="element-506">
                          Built on secure cloud infrastructure ensuring data
                          encryption in transit and at rest, supporting public
                          sector procurement requirements.
                        </p>
                      </div>
                      <div className="p-6 bg-federal-blue/5 border border-federal-blue/10 rounded-sm" data-id="element-507">
                        <div className="flex items-center gap-3 mb-2" data-id="element-508">
                          <ShieldCheck className="w-5 h-5 text-federal-blue" data-id="element-509" />
                          <span className="text-xs font-black text-federal-blue uppercase tracking-widest" data-id="element-510">
                            Secure Deployment
                          </span>
                        </div>
                        <p className="text-[10px] text-tech-slate font-bold uppercase opacity-60" data-id="element-511">
                          NIST 800-171 Compliant Infrastructure
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </SectionWrapper>

        {/* CTA Section */}
        <SectionWrapper data-id="element-512">
          <section className="py-20 bg-white" data-id="element-513">
            <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-514">
              <div className="bg-federal-blue p-12 rounded-sm text-white flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-515">
                <div className="max-w-xl" data-id="element-516">
                  <h2 className="text-3xl font-black mb-4" data-id="element-517">
                    Ready to Automate Your Compliance?
                  </h2>
                  <p className="text-white/70" data-id="element-518">
                    Our engineers are ready to demonstrate how Comply+ can
                    secure your agency's digital footprint.
                  </p>
                </div>
                <motion.button whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.97
              }} className="bg-muted-gold hover:bg-gold-hover text-white px-8 py-4 font-bold rounded-sm whitespace-nowrap transition-colors" data-id="element-519">
                  Request Platform Demo
                </motion.button>
              </div>
            </div>
          </section>
        </SectionWrapper>
      </div>
    </PageWrapper>;
}